import { createFileRoute } from "@tanstack/react-router";
import { fetchAllRows, fetchByIds, fetchOrgMembers } from "@/lib/large-org";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/lib/auth";
import { useAccessibleTabs } from "@/components/ui/a11y-tabs";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Radio, Send, CheckCircle2, Clock, AlertOctagon } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { preflightAssignment } from "@/lib/compliance";
import {
  acknowledgeBroadcast,
  updateAssignmentStatus,
  sendBroadcast,
  createAssignment,
} from "@/lib/dispatch.functions";

export const Route = createFileRoute("/_authenticated/console/dispatch")({
  component: DispatchPage,
  head: () => ({ meta: [{ title: "Dispatch — MissionReady" }] }),
});

type Member = { user_id: string; role: string; profile?: { full_name: string | null; email: string | null } };
type Site = { id: string; name: string };
type Broadcast = { id: string; title: string; message: string; priority: string; sender_id: string; created_at: string; requires_ack: boolean };
type Assignment = { id: string; task: string; details: string | null; status: string; priority: string; officer_id: string; created_at: string; due_at: string | null };

const PRIO_COLOR: Record<string, string> = {
  critical: "border-destructive/40 bg-destructive/10 text-destructive",
  urgent: "border-warning/40 bg-warning/10 text-warning",
  normal: "border-border bg-card",
};

function DispatchPage() {
  const { currentOrg, user, currentRole } = useAuth();
  const [tab, setTab] = useState<"broadcast" | "assign">("broadcast");
  const a11yTabs = useAccessibleTabs(["broadcast", "assign"] as const, tab, setTab);
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [sites, setSites] = useState<Site[]>([]);

  const canDispatch = ["org_owner", "admin", "supervisor", "dispatcher"].includes(currentRole ?? "");

  useEffect(() => {
    if (!currentOrg) return;
    const orgId = currentOrg.id;
    const load = async () => {
      const [b, a, m, s] = await Promise.all([
        supabase.from("dispatch_broadcasts").select("*").eq("organization_id", orgId).order("created_at", { ascending: false }).limit(50),
        supabase.from("dispatch_assignments").select("*").eq("organization_id", orgId).order("created_at", { ascending: false }).limit(50),
        fetchOrgMembers(orgId).then((data) => ({ data })),
        supabase.from("sites").select("id, name").eq("organization_id", orgId),
      ]);
      setBroadcasts((b.data as Broadcast[]) ?? []);
      setAssignments((a.data as Assignment[]) ?? []);
      setSites((s.data as Site[]) ?? []);
      const ids = ((m.data as any[]) ?? []).map((x) => x.user_id);
      if (ids.length) {
        const profs = await fetchByIds<any>("profiles", "id, full_name, email", ids);
        const map = new Map(((profs as any[]) ?? []).map((p) => [p.id, p]));
        setMembers(((m.data as any[]) ?? []).map((mm) => ({ ...mm, profile: map.get(mm.user_id) })));
      } else setMembers([]);
    };
    load();
    const ch = supabase.channel(`dispatch:${orgId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "dispatch_broadcasts", filter: `organization_id=eq.${orgId}` }, load)
      .on("postgres_changes", { event: "*", schema: "public", table: "dispatch_assignments", filter: `organization_id=eq.${orgId}` }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [currentOrg]);

  const memberName = (uid: string) => {
    const m = members.find((x) => x.user_id === uid);
    return m?.profile?.full_name || m?.profile?.email || uid.slice(0, 8);
  };

  return (
    <div className="px-4 py-6 md:px-8 md:py-8">
      <div className="flex items-center gap-2">
        <Radio className="h-5 w-5 text-primary" />
        <h1 className="font-display text-2xl font-bold md:text-3xl">Dispatch</h1>
      </div>
      <p className="text-sm text-muted-foreground">Broadcast org-wide alerts or assign tasks to specific officers in real time.</p>

      <div className="mt-6 inline-flex rounded-lg border border-border p-1" aria-label="Dispatch views" {...a11yTabs.tablistProps}>
        <button {...a11yTabs.tabProps("broadcast")} onClick={() => setTab("broadcast")}
          className={`rounded-md px-4 py-1.5 text-sm font-medium ${tab === "broadcast" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
          Broadcasts
        </button>
        <button {...a11yTabs.tabProps("assign")} onClick={() => setTab("assign")}
          className={`rounded-md px-4 py-1.5 text-sm font-medium ${tab === "assign" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
          Assignments
        </button>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_1.4fr]" {...a11yTabs.panelProps(tab)}>
        {tab === "broadcast" ? (
          <>
            {canDispatch && currentOrg && user && (
              <BroadcastComposer orgId={currentOrg.id} userId={user.id} sites={sites} />
            )}
            <div className="space-y-2">
              {broadcasts.length === 0 && <p className="text-sm text-muted-foreground">No broadcasts yet.</p>}
              {broadcasts.map((b) => (
                <div key={b.id} className={`rounded-xl border p-4 ${PRIO_COLOR[b.priority] ?? PRIO_COLOR.normal}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-semibold">{b.title}</div>
                    <span className="text-xs uppercase">{b.priority}</span>
                  </div>
                  <p className="mt-1 text-sm text-foreground/80">{b.message}</p>
                  <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                    <span>{memberName(b.sender_id)}</span>
                    <span>{formatDistanceToNow(new Date(b.created_at), { addSuffix: true })}</span>
                  </div>
                  {b.requires_ack && currentOrg && user && (
                    <AckButton orgId={currentOrg.id} broadcastId={b.id} userId={user.id} />
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            {canDispatch && currentOrg && user && (
              <AssignmentComposer orgId={currentOrg.id} userId={user.id} members={members} sites={sites} />
            )}
            <div className="space-y-2">
              {assignments.length === 0 && <p className="text-sm text-muted-foreground">No assignments yet.</p>}
              {assignments.map((a) => (
                <div key={a.id} className={`rounded-xl border p-4 ${PRIO_COLOR[a.priority] ?? PRIO_COLOR.normal}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-semibold">{a.task}</div>
                      <div className="text-xs text-muted-foreground">→ {memberName(a.officer_id)}</div>
                    </div>
                    <StatusBadge status={a.status} />
                  </div>
                  {a.details && <p className="mt-2 text-sm">{a.details}</p>}
                  <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                    <span>{formatDistanceToNow(new Date(a.created_at), { addSuffix: true })}</span>
                    {(a.officer_id === user?.id || canDispatch) && (
                      <AssignmentActions assignment={a} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { icon: any; cls: string }> = {
    pending: { icon: Clock, cls: "text-muted-foreground" },
    accepted: { icon: CheckCircle2, cls: "text-primary" },
    en_route: { icon: Send, cls: "text-primary" },
    on_scene: { icon: AlertOctagon, cls: "text-warning" },
    completed: { icon: CheckCircle2, cls: "text-success" },
    cancelled: { icon: AlertOctagon, cls: "text-muted-foreground" },
  };
  const m = map[status] ?? map.pending;
  const Icon = m.icon;
  return <span className={`inline-flex items-center gap-1 text-xs font-medium ${m.cls}`}><Icon className="h-3 w-3" />{status}</span>;
}

function AckButton({ orgId, broadcastId, userId }: { orgId: string; broadcastId: string; userId: string }) {
  const [acked, setAcked] = useState(false);
  const ack = useServerFn(acknowledgeBroadcast);
  useEffect(() => {
    supabase.from("dispatch_acknowledgements").select("id").eq("broadcast_id", broadcastId).eq("user_id", userId).maybeSingle()
      .then(({ data }) => setAcked(!!data));
  }, [broadcastId, userId]);
  if (acked) return <div className="mt-2 text-xs text-success">✓ Acknowledged</div>;
  return (
    <Button size="sm" variant="outline" className="mt-2" onClick={async () => {
      try {
        await ack({ data: { orgId, broadcastId } });
        setAcked(true);
      } catch (e: any) { toast.error(e?.message ?? "Failed"); }
    }}>Acknowledge</Button>
  );
}

function AssignmentActions({ assignment }: { assignment: Assignment }) {
  const updateStatus = useServerFn(updateAssignmentStatus);
  const next: Record<string, string[]> = {
    pending: ["accepted", "cancelled"],
    accepted: ["en_route", "cancelled"],
    en_route: ["on_scene", "cancelled"],
    on_scene: ["completed"],
  };
  const opts = next[assignment.status] ?? [];
  if (opts.length === 0) return null;
  return (
    <div className="flex gap-1">
      {opts.map((s) => (
        <button key={s} onClick={async () => {
          try {
            await updateStatus({ data: { assignmentId: assignment.id, status: s as any } });
          } catch (e: any) { toast.error(e?.message ?? "Failed"); }
        }} className="rounded border border-border px-2 py-0.5 text-xs hover:bg-muted">
          {s.replace("_", " ")}
        </button>
      ))}
    </div>
  );
}

function BroadcastComposer({ orgId, sites }: { orgId: string; userId: string; sites: Site[] }) {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [priority, setPriority] = useState("normal");
  // "all" is the sentinel for "no specific site" — Radix Select forbids an
  // empty-string item value, so the sentinel is translated on submit.
  const [siteId, setSiteId] = useState<string>("all");
  const [requiresAck, setRequiresAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const send = useServerFn(sendBroadcast);

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <h2 className="mb-4 font-display text-lg font-semibold">New broadcast</h2>
      <div className="space-y-3">
        <div><Label>Title</Label><Input aria-label="Title" value={title} onChange={(e) => setTitle(e.target.value)} /></div>
        <div><Label>Message</Label><Textarea aria-label="Message" value={message} onChange={(e) => setMessage(e.target.value)} rows={4} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger aria-label="Priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Site (optional)</Label>
            <Select value={siteId} onValueChange={setSiteId}>
              <SelectTrigger aria-label="Site (optional)"><SelectValue placeholder="All sites" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sites</SelectItem>
                {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" className="h-6 w-6 accent-[currentColor]" checked={requiresAck} onChange={(e) => setRequiresAck(e.target.checked)} />
          Require acknowledgement
        </label>
        <Button disabled={busy || !title || !message} onClick={async () => {
          setBusy(true);
          try {
            const res = await send({ data: { orgId, title, message, priority: priority as any, siteId: siteId === "all" ? null : siteId, requiresAck } });
            const scope = siteId === "all" ? "All Sites" : (sites.find((s) => s.id === siteId)?.name ?? "the selected site");
            const n = res?.recipientCount ?? null;
            toast.success(
              typeof n === "number"
                ? `Broadcast sent to ${n} recipient${n === 1 ? "" : "s"} ${siteId === "all" ? "across" : "at"} ${scope}.`
                : "Broadcast created successfully and is being delivered to eligible recipients.",
            );
            setTitle(""); setMessage(""); setRequiresAck(false);
          } catch (e: any) { toast.error(e?.message ?? "Failed"); }
          setBusy(false);
        }}><Send className="mr-1 h-4 w-4" /> Send broadcast</Button>
      </div>
    </div>
  );
}

function AssignmentComposer({ orgId, members, sites }: { orgId: string; userId: string; members: Member[]; sites: Site[] }) {
  const [task, setTask] = useState("");
  const [details, setDetails] = useState("");
  const [officerId, setOfficerId] = useState("");
  // "none" is the sentinel for "no site" (Radix forbids empty-string items).
  const [siteId, setSiteId] = useState("none");
  const [priority, setPriority] = useState("normal");
  const create = useServerFn(createAssignment);

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <h2 className="mb-4 font-display text-lg font-semibold">New assignment</h2>
      <div className="space-y-3">
        <div><Label>Task</Label><Input aria-label="Task" value={task} onChange={(e) => setTask(e.target.value)} placeholder="e.g. Respond to alarm at gate B" /></div>
        <div><Label>Details</Label><Textarea aria-label="Details" value={details} onChange={(e) => setDetails(e.target.value)} rows={3} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Assign to</Label>
            <Select value={officerId} onValueChange={setOfficerId}>
              <SelectTrigger aria-label="Assign to"><SelectValue placeholder="Select officer" /></SelectTrigger>
              <SelectContent>
                {members.map((m) => (
                  <SelectItem key={m.user_id} value={m.user_id}>
                    {m.profile?.full_name || m.profile?.email || m.user_id.slice(0, 8)} · {m.role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger aria-label="Priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>Site (optional)</Label>
          <Select value={siteId} onValueChange={setSiteId}>
            <SelectTrigger aria-label="Site (optional)"><SelectValue placeholder="None" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button disabled={!task || !officerId} onClick={async () => {
          try {
            const ev = await preflightAssignment({ organization_id: orgId, officer_id: officerId, site_id: siteId === "none" ? null : siteId });
            if (ev.status === "blocked" && ev.enforcement_mode === "strict") {
              const why = ev.outcomes.find((o) => o.enforcement === "block" && o.status !== "ok")?.message ?? "missing required credentials";
              toast.error(`Blocked by compliance: ${why}`);
              return;
            }
            if (ev.status === "warning") toast.warning("Compliance warning — dispatch allowed");
          } catch (e) { console.warn(e); }
          try {
            await create({ data: { orgId, officerId, task, details: details || undefined, priority: priority as any, siteId: siteId === "none" ? null : siteId } });
            toast.success("Assignment dispatched"); setTask(""); setDetails("");
          } catch (e: any) { toast.error(e?.message ?? "Failed"); }
        }}><Send className="mr-1 h-4 w-4" /> Dispatch</Button>
      </div>
    </div>
  );
}
